import Input from "src/components/inputs/Input";
import SensitiveTextarea from "src/components/inputs/SensitiveTextarea";
import { ClipboardIcon } from "@heroicons/react/24/outline";
import { twMerge } from "tailwind-merge";
import ConnectionStatusBlock from "./ConnectionStatusBlock";

interface EpicConfigTabProps {
  clientId: string;
  onClientIdChange: (value: string) => void;
  epicFrontendClientId: string;
  onEpicFrontendClientIdChange: (value: string) => void;
  epicClientSecret: string;
  onEpicClientSecretChange: (value: string) => void;
  baseOauthUrl: string;
  onBaseOauthUrlChange: (value: string) => void;
  jwksUrl: string;
  jwksCopied: boolean;
  onJwksCopy: () => void;
  disableInputs: boolean;
  connectionStatus: boolean | null;
  isTestingConnection: boolean;
  isLoadingConnectionStatus: boolean;
  onTestConnection: () => Promise<void>;
}

const EpicConfigTab = ({
  clientId,
  onClientIdChange,
  epicFrontendClientId,
  onEpicFrontendClientIdChange,
  epicClientSecret,
  onEpicClientSecretChange,
  baseOauthUrl,
  onBaseOauthUrlChange,
  jwksUrl,
  jwksCopied,
  onJwksCopy,
  disableInputs,
  connectionStatus,
  isTestingConnection,
  isLoadingConnectionStatus,
  onTestConnection,
}: EpicConfigTabProps) => (
  <>
    <h3 className="text-lg font-medium text-gray-900">Epic Configuration</h3>
    <ConnectionStatusBlock
      connectionStatus={connectionStatus}
      isTestingConnection={isTestingConnection}
      isLoadingConnectionStatus={isLoadingConnectionStatus}
    />
    <div className="flex gap-4 mb-4">
      <button
        type="button"
        disabled={disableInputs || isTestingConnection}
        onClick={() => onTestConnection()}
        className="max-w-xs w-full bg-brand-600 hover:bg-brand-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-medium py-3 px-4 rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2"
      >
        {isTestingConnection ? "Testing..." : "Test Connection"}
      </button>
    </div>
    <Input
      type="text"
      label="Client ID"
      placeholder="Enter Client ID"
      value={clientId}
      onChange={(e) => onClientIdChange(e.target.value)}
      required={false}
      disabled={disableInputs}
    />
    <Input
      type="text"
      label="Frontend Client ID"
      placeholder="Enter Frontend Client ID (for OAuth flows)"
      value={epicFrontendClientId}
      onChange={(e) => onEpicFrontendClientIdChange(e.target.value)}
      required={false}
      disabled={disableInputs}
    />
    <SensitiveTextarea
      label="Epic Client Secret"
      placeholder="Enter Epic Client Secret"
      value={epicClientSecret}
      onChange={onEpicClientSecretChange}
      required={false}
      disabled={disableInputs}
    />
    {typeof window !== "undefined" && (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <div className="ml-3 flex-1">
            <h3 className="text-sm font-medium text-blue-800">JWK Set URL</h3>
            <div className="mt-2 text-sm text-blue-700">
              <div className="flex items-center break-all font-mono bg-white px-3 py-2 rounded border border-blue-200 justify-between">
                <span>{jwksUrl}</span>
                <div className="flex items-center gap-2">
                  {jwksCopied && (
                    <span className="text-xs text-green-600 font-medium text-nowrap">Copied!</span>
                  )}
                  <ClipboardIcon
                    className={twMerge("w-4 h-4 cursor-pointer stroke-2", jwksCopied && "text-green-500")}
                    onClick={onJwksCopy}
                  />
                </div>
              </div>
              <p className="mt-2">
                Provide this URL to Epic when configuring your application. This endpoint contains your public keys for JWT verification.
              </p>
            </div>
          </div>
        </div>
      </div>
    )}
    <Input
      type="text"
      label="OAuth URL"
      placeholder="Enter Base OAuth URL (R4 and STU3 paths are derived from this)"
      value={baseOauthUrl}
      onChange={(e) => onBaseOauthUrlChange(e.target.value)}
      required={false}
      disabled={disableInputs}
    />
  </>
);

export default EpicConfigTab;
